import { useState, useEffect } from 'react'

interface CurrencyInfo {
  code: string
  symbol: string
  rate: number
  locale: string
}

const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  US: { code: 'USD', symbol: '$', rate: 1, locale: 'en-US' },
  MX: { code: 'MXN', symbol: '$', rate: 17.5, locale: 'es-MX' },
  CO: { code: 'COP', symbol: '$', rate: 4200, locale: 'es-CO' },
  AR: { code: 'ARS', symbol: '$', rate: 900, locale: 'es-AR' },
  CL: { code: 'CLP', symbol: '$', rate: 950, locale: 'es-CL' },
  PE: { code: 'PEN', symbol: 'S/', rate: 3.75, locale: 'es-PE' },
  BR: { code: 'BRL', symbol: 'R$', rate: 5.0, locale: 'pt-BR' },
  ES: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'es-ES' },
  FR: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'fr-FR' },
  DE: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'de-DE' },
  IT: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'it-IT' },
  GB: { code: 'GBP', symbol: '£', rate: 0.79, locale: 'en-GB' },
  CA: { code: 'CAD', symbol: 'CA$', rate: 1.36, locale: 'en-CA' },
  EC: { code: 'USD', symbol: '$', rate: 1, locale: 'es-EC' },
  PA: { code: 'USD', symbol: '$', rate: 1, locale: 'es-PA' },
  SV: { code: 'USD', symbol: '$', rate: 1, locale: 'es-SV' },
  GT: { code: 'GTQ', symbol: 'Q', rate: 7.75, locale: 'es-GT' },
  HN: { code: 'HNL', symbol: 'L', rate: 24.7, locale: 'es-HN' },
  CR: { code: 'CRC', symbol: '₡', rate: 530, locale: 'es-CR' },
  DO: { code: 'DOP', symbol: 'RD$', rate: 57, locale: 'es-DO' },
  UY: { code: 'UYU', symbol: '$U', rate: 39, locale: 'es-UY' },
  PY: { code: 'PYG', symbol: '₲', rate: 7300, locale: 'es-PY' },
  BO: { code: 'BOB', symbol: 'Bs', rate: 6.9, locale: 'es-BO' },
  VE: { code: 'USD', symbol: '$', rate: 1, locale: 'es-VE' },
  NI: { code: 'NIO', symbol: 'C$', rate: 36.5, locale: 'es-NI' },
}

const DEFAULT_CURRENCY: CurrencyInfo = {
  code: 'USD',
  symbol: '$',
  rate: 1,
  locale: 'en-US',
}

export function useCurrency() {
  const [currency, setCurrency] = useState<CurrencyInfo>(DEFAULT_CURRENCY)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function detectCurrency() {
      try {
        const res = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(4000),
        })
        if (!res.ok) throw new Error('API error')
        const data = await res.json()
        const countryCode = data.country_code || data.country

        if (!cancelled && countryCode) {
          const detected = CURRENCY_MAP[countryCode]
          if (detected) {
            setCurrency(detected)
          }
        }
      } catch {
        // Silently fallback to USD
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    detectCurrency()
    return () => {
      cancelled = true
    }
  }, [])

  function formatPrice(usdAmount: number): string {
    const converted = usdAmount * currency.rate

    // For currencies with large values, no decimals
    if (currency.rate >= 100) {
      return `${currency.symbol}${Math.round(converted).toLocaleString(currency.locale)} ${currency.code}`
    }

    return `${currency.symbol}${converted.toFixed(2)} ${currency.code}`
  }

  return { currency, loading, formatPrice }
}
